import { Component, OnInit, signal, computed } from '@angular/core';
import { finalize, take } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GameService } from '../../shared/services/game.service';
import { ScheduleService } from '../../shared/services/schedule.service';
import { GamePageViewModel } from '../../shared/models/GameModels';
import { CommonModule, UpperCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GroupByDateAndDivisionPipe } from '../../shared/pipes/group-by-date-and-division.pipe';
import { SetGameFilterDialogComponent } from '../../game/set-game-filter/set-game-filter-dialog.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { AddToScheduleDialogComponent } from '../components/add-to-schedule-dialog/add-to-schedule-dialog.component';

@Component({
  standalone: true,
  selector: 'app-game-list',
  templateUrl: './game-list.component.html',
  styleUrls: ['./game-list.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    MatIconModule,
    MatToolbarModule,
    MatProgressBarModule,
    MatButtonModule,
    MatTableModule,
    MatMenuModule,
    ReactiveFormsModule,
    FormsModule,
    GroupByDateAndDivisionPipe,
    

]
})
export class GameListComponent implements OnInit {
  _pageData = signal<GamePageViewModel>({ DivisionFilter: [], Games: [] } as GamePageViewModel);
  pastGamesActive = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  useLocalTimezone = signal<boolean>(true);
  GroupByPropertyName = signal<string>("GameDate");
  sort = signal<'asc' | 'desc'>('asc');

  timezoneText = computed(() => 
    this.GroupByPropertyName() === 'GameTimeUTC' 
      ? this.translate.instant('TIMEZONE.MY_TIMEZONE') 
      : this.translate.instant('TIMEZONE.LOCAL_TIMEZONES')
  );

  constructor(
    public gameService: GameService,
    private scheduleService: ScheduleService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.getData();
    this.pastGamesActive.set(false);
    this.GroupByPropertyName.set("GameDate");
  }

  getData() {
    this.isLoading.set(true);
    this.pastGamesActive.set(false);
    this.sort.set('desc');
    this.gameService.getGames()
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (data) => {
          this._pageData.set(data);
          // console.log('Game data:', data); // Felsökning
        },
        error: (error) => {
          // console.error('Error loading games:', error);
          this.openSnackBar('Error loading games', '');
        }
      });
  }

  getPastGames() {
    this.isLoading.set(true);
    this.pastGamesActive.set(true);
    this.sort.set('asc');
    
    // console.log('Starting getPastGames...');
    
    this.gameService.getPastGames()
      .pipe(
        take(1),
        // tap(data => console.log('Data received:', data)),
        finalize(() => {
          // console.log('Finalize called, setting isLoading to false');
          this.isLoading.set(false);
        })
      )
      .subscribe({
        next: (data) => {
          // console.log('Subscribe next called');
          this._pageData.set(data);
        },
        error: (error) => {
          // console.error('Error loading past games:', error);
          this.openSnackBar('Error loading past games', '');
        },
        complete: () => {
          // console.log('Observable completed');
        }
      });
  }

  openSetGameFilterDialog(): void {
    const dialogRef = this.dialog.open(SetGameFilterDialogComponent, {
      width: '600px'
    });

    dialogRef.componentInstance.divisionChanged
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(() => {
        this.getData();
      });

    dialogRef.componentInstance.showAllDivisionChanged
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(() => {
        this.getData();
      });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.getData();
        }
      });
  }

  addToSchedule(GameId: string, HomeTeam: string, AwayTeam: string, GameDate: Date, GameTime: Date) {
    const dialogRef = this.dialog.open(AddToScheduleDialogComponent, {
      width: '350px',
      data: {
        GameId: GameId,
        GameDate: GameDate,
        GameTime: GameTime,
        HomeTeam: HomeTeam,
        AwayTeam: AwayTeam
      }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        this.openSnackBar("EVENT ADDED", "");
        if (this.pastGamesActive()) {
          this.getPastGames();
        } else {
          this.getData();
        }
      });
  }

  removeFromSchedule(GameId: string) {
    this.openSnackBar("EVENT REMOVED", "");
    this.scheduleService.removeEventFromSchedule(GameId)
      .pipe(take(1))
      .subscribe(() => {
        if (this.pastGamesActive()) {
          this.getPastGames();
        } else {
          this.getData();
        }
      });
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000,
    });
  }

  changeBgColor(gameId: string) {
    const element = document.getElementById(gameId);
    if (element) {
      element.style.backgroundColor = '#f0f0f0';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 200);
    }
  }

  getIconPath(icon: string): string {

    icon = icon.toLowerCase();
    console.log('getIconPath called with icon:', icon);

    if (icon.startsWith('/')) {
      return `../assets/img${icon}`;
    } else {
      return `../assets/img/${icon}`;
    }
  }

  handleImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = '../assets/img/placeholder.png';
    imgElement.style.width = '30px';
    imgElement.style.height = '30px';
    imgElement.style.objectFit = 'contain';
  }

  toggleTimezone(timezone: string) {
    this.GroupByPropertyName.set(timezone);
    this.useLocalTimezone.set(timezone === 'GameDate');
    if (this.pastGamesActive()) {
      this.getPastGames();
    } else {
      this.getData();
    }
  }
}